import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare } from "lucide-react";
import ParallaxBackground from "./ParallaxBackground";
import { trackWhatsAppClick, captureLead, trackFormStart } from "@/lib/analytics";

export default function WhatsAppLeadCapture() {
    const [name, setName] = useState("");
    const [month, setMonth] = useState("");
    const [groupSize, setGroupSize] = useState("");
    const [email, setEmail] = useState("");
    const [whatsapp, setWhatsapp] = useState("");
    const { toast } = useToast();

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!name || !month || !groupSize || !whatsapp || !email) {
            toast({
                variant: "destructive",
                title: "Missing Information",
                description: "Please fill in all fields to get your free plan.",
            });
            return;
        }

        const message = `*New Safari Plan Request*\n\nName: ${name}\nEmail: ${email}\nTravel Month: ${month}\nGroup Size: ${groupSize}\nWhatsApp: ${whatsapp}\n\nPlease help me plan my safari!`;

        // Capture lead in database
        captureLead({
            email,
            name,
            phone: whatsapp,
            source: 'Lead Capture Form'
        });

        trackWhatsAppClick('Lead Capture Form');
        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/256780628299?text=${encodedMessage}`, '_blank');
    };

    return (
        <section className="py-20 bg-gray-50/50 relative overflow-hidden group">
            <ParallaxBackground intensity={5} variant="primary" />

            <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-4xl mx-auto bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-gray-100 transition-all duration-500 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)]">
                    <div className="p-8 md:p-12 relative overflow-hidden">
                        {/* Decorative circle */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />

                        <div className="relative z-10 text-center mb-10">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-heading">
                                Get a Free Safari Plan in 24 Hours
                            </h2>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                Tell us a little about your dream trip, and our safari experts will craft a personalized itinerary just for you.
                            </p>
                        </div>

                        <div className="bg-transparent p-2 text-left">
                            <form onSubmit={handleSubmit} className="space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <Input
                                        placeholder="Your Name"
                                        value={name}
                                        onFocus={() => trackFormStart('Lead Capture Form')}
                                        onChange={(e) => setName(e.target.value)}
                                        className="bg-white border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/20 h-12"
                                    />
                                    <Input
                                        placeholder="Email Address"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="bg-white border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/20 h-12"
                                    />
                                    <Select onValueChange={setMonth} value={month}>
                                        <SelectTrigger className="bg-white border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/20 h-12">
                                            <SelectValue placeholder="Travel Month" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((m) => (
                                                <SelectItem key={m} value={m}>{m}</SelectItem>
                                            ))}
                                            <SelectItem value="Not decided">Not decided</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        placeholder="Group Size"
                                        type="number"
                                        min="1"
                                        value={groupSize}
                                        onChange={(e) => setGroupSize(e.target.value)}
                                        className="bg-white border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/20 h-12"
                                    />
                                    <Input
                                        placeholder="WhatsApp Number"
                                        value={whatsapp}
                                        onChange={(e) => setWhatsapp(e.target.value)}
                                        className="bg-white border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary/20 h-12 md:col-span-2 lg:col-span-1"
                                    />
                                </div>
                                <div className="flex justify-center">
                                    <Button type="submit" className="w-full md:w-auto md:min-w-[240px] h-12 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold text-base shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 px-10">
                                        <MessageSquare className="w-5 h-5 mr-3" />
                                        Get My Safari Plan
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
